/**
 * Set V2 Token Utility
 * 
 * Stores authentication token in the same cookie format as v2-frontend (new_demo_frontend)
 * This allows front_compliance to share authentication with v2-frontend
 */

const MAX_COOKIE_SIZE = 4000; // Each cookie has a ~4KB limit
const COOKIE_PREFIX = 'd_c_c_t_p_'; // Cookie prefix used by v2-frontend

/**
 * Helper to split a string into chunks
 */
function splitString(str: string, chunkSize: number): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.substring(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Get the domain for cookies (same as landing and v2-frontend)
 */
function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const envDomain = process.env.REACT_APP_DOMAIN;
  if (envDomain) return envDomain;
  // For localhost, we can't set domain, so return undefined
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  // Extract base domain (e.g., 'pianat.ai' from 'demo.pianat.ai')
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return undefined;
}

/**
 * Stores the authentication token in the same cookie format as v2-frontend.
 * Splits large tokens across multiple cookies (d_c_c_t_p_1, d_c_c_t_p_2, etc.)
 */
export function setV2Token(token: string, rememberMe: boolean = false): void {
  if (typeof window === "undefined" || !token) {
    return;
  }

  // Clear any existing token parts first
  clearV2Token();

  // Encode the token to handle special characters
  const encodedToken = encodeURIComponent(token);
  
  // Split into chunks that fit in cookies
  const tokenParts = splitString(encodedToken, MAX_COOKIE_SIZE);
  const domain = getCookieDomain();
  const maxAge = rememberMe ? 'max-age=31536000' : ''; // 1 year if remember me, session cookie otherwise
  const domainPart = domain ? `; domain=${domain}` : '';

  // Store each part in a separate cookie (same format as v2-frontend and landing)
  tokenParts.forEach((part, index) => {
    const cookieName = `${COOKIE_PREFIX}${index + 1}`;
    const cookieValue = `${cookieName}=${part}; path=/; secure; samesite=strict${maxAge ? `; ${maxAge}` : ''}${domainPart}`;
    document.cookie = cookieValue;
  });
}

/**
 * Clears all authentication token parts from cookies.
 */
export function clearV2Token(domain?: string): void {
  if (typeof window === "undefined") return;
  
  // Use provided domain or get from environment (same as landing and v2-frontend)
  const cookieDomain = domain || getCookieDomain();
  const domainPart = cookieDomain ? `; domain=${cookieDomain}` : '';
  
  let partIndex = 1;

  // Helper to get a cookie value by name
  function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
  }

  while (getCookie(`${COOKIE_PREFIX}${partIndex}`)) {
    // Same format as v2-frontend and landing
    document.cookie = `${COOKIE_PREFIX}${partIndex}=; path=/; secure; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT;${domainPart}`;
    partIndex++;
  }
}

